"use client";

import { useEffect, useMemo, useState } from "react";
import styles from "./WordExpandableText.module.scss";

interface WordExpandableTextProps {
  text: string;
  wordLimit: number;
  mobileOnly?: boolean;
  className?: string;
  buttonClassName?: string;
  expandedClassName?: string;
  onReadMoreClick?: () => void;
}

const getWords = (text: string) => text.trim().split(/\s+/).filter(Boolean);

export default function WordExpandableText({
  text,
  wordLimit,
  mobileOnly = false,
  className = "",
  buttonClassName = "",
  expandedClassName = "",
  onReadMoreClick,
}: WordExpandableTextProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);

    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const words = useMemo(() => getWords(text), [text]);
  const shouldClamp =
    isHydrated && words.length > wordLimit && (!mobileOnly || isMobile);

  if (!shouldClamp) {
    return <p className={`${className} ${expandedClassName}`}>{text}</p>;
  }

  const truncatePreserveNewLines = (text: string, wordLimit: number) => {
  const words = text.match(/\S+/g) || [];

  if (words.length <= wordLimit) return text;

  let count = 0;
  let endIndex = text.length;

  text.replace(/\S+/g, (match, offset) => {
    count++;
    if (count === wordLimit + 1) {
      endIndex = offset;
      return match;
    }
    return match;
  });

  return `${text.slice(0, endIndex).trim()}...`;
};

const truncatedText = truncatePreserveNewLines(text, wordLimit);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (onReadMoreClick) {
      onReadMoreClick();
      return;
    }

    setIsExpanded((value) => !value);
  };

  return (
    <div className={styles.wrapper}>
      <p className={`${className} ${isExpanded ? expandedClassName : ""}`}>
        {isExpanded ? text : truncatedText}{" "}
        <button
          type="button"
          className={`${styles.button} ${buttonClassName}`}
          onClick={handleButtonClick}
        >
          {onReadMoreClick ? "Read more" : isExpanded ? "Read less" : "Read more"}
        </button>
      </p>
    </div>
  );
}
